import { Icons } from "./icons";
import { TOOL_DEFS } from "../drawings/registry";
import { SHORTCUTS } from "../input/shortcuts";

interface Group {
  id: string;
  tools: string[]; // tool ids; first is default
  current?: string;
}

const GROUPS: Group[] = [
  { id: "cursor", tools: ["cursor"] },
  { id: "position", tools: ["position"] },
  { id: "lines", tools: ["trendline", "ray", "extended", "arrow", "hline", "vline"] },
  { id: "shapes", tools: ["rect", "ellipse", "channel"] },
  { id: "fib", tools: ["fibretr", "fibext"] },
  { id: "measure", tools: ["pricerange", "daterange", "riskreward"] },
];

const TOOL_ICON: Record<string, string> = {
  cursor: "cursor",
  position: "position",
  trendline: "trendline",
  ray: "ray",
  extended: "extended",
  arrow: "arrow",
  hline: "hline",
  vline: "vline",
  rect: "rect",
  ellipse: "ellipse",
  channel: "channel",
  fibretr: "fibretr",
  fibext: "fibext",
  pricerange: "pricerange",
  daterange: "daterange",
  riskreward: "riskreward",
};

const COMBO_BY_TOOL: Record<string, string> = {};
for (const s of SHORTCUTS) {
  if (s.action.startsWith("tool:")) COMBO_BY_TOOL[s.action.slice(5)] = s.keys.join(" ");
}

function labelOf(id: string): string {
  return id === "cursor" ? "Cursor" : TOOL_DEFS[id]?.label ?? id;
}
function comboOf(id: string): string {
  return id === "cursor" ? "Esc" : COMBO_BY_TOOL[id] ?? "";
}

export class DrawingToolbar {
  el: HTMLElement;
  private onSelect: (tool: string) => void;
  private onMagnet: (on: boolean) => void;
  private onRemoveAll: () => void;
  private getActive: () => string;
  private buttons = new Map<string, HTMLElement>();
  private flyout: HTMLElement | null = null;
  private magnetOn = false;

  constructor(
    el: HTMLElement,
    opts: {
      onSelect: (tool: string) => void;
      onMagnet: (on: boolean) => void;
      onRemoveAll: () => void;
      getActive: () => string;
      initialMagnet?: boolean;
    }
  ) {
    this.el = el;
    this.onSelect = opts.onSelect;
    this.onMagnet = opts.onMagnet;
    this.onRemoveAll = opts.onRemoveAll;
    this.getActive = opts.getActive;
    this.magnetOn = opts.initialMagnet ?? false;
    this.build();
    document.addEventListener("pointerdown", (e) => {
      if (this.flyout && !this.flyout.contains(e.target as Node)) this.closeFlyout();
    });
  }

  private build() {
    this.el.innerHTML = "";
    for (const g of GROUPS) {
      g.current = g.current ?? g.tools[0];
      const btn = document.createElement("button");
      btn.className = "tool-btn";
      btn.title = labelOf(g.current);
      btn.innerHTML = Icons[TOOL_ICON[g.current!]];
      if (g.tools.length > 1) {
        const caret = document.createElement("span");
        caret.className = "flycaret";
        btn.appendChild(caret);
      }
      btn.addEventListener("click", (e) => {
        const r = btn.getBoundingClientRect();
        const inCaret = g.tools.length > 1 && e.clientX > r.right - 12 && e.clientY > r.bottom - 12;
        if (inCaret) {
          this.openFlyout(g, btn);
        } else {
          this.onSelect(g.current!);
        }
      });
      this.el.appendChild(btn);
      this.buttons.set(g.id, btn);
    }

    const sep = document.createElement("div");
    sep.className = "tool-sep";
    this.el.appendChild(sep);

    // magnet
    const magnet = document.createElement("button");
    magnet.className = "tool-btn" + (this.magnetOn ? " active" : "");
    magnet.title = "Magnet (snap to price)";
    magnet.innerHTML = Icons.magnet;
    magnet.addEventListener("click", () => {
      this.magnetOn = !this.magnetOn;
      magnet.classList.toggle("active", this.magnetOn);
      this.onMagnet(this.magnetOn);
    });
    this.el.appendChild(magnet);

    // remove all
    const trash = document.createElement("button");
    trash.className = "tool-btn";
    trash.title = "Remove all drawings";
    trash.innerHTML = Icons.trash;
    trash.addEventListener("click", () => this.onRemoveAll());
    this.el.appendChild(trash);
  }

  private openFlyout(g: Group, btn: HTMLElement) {
    this.closeFlyout();
    const fly = document.createElement("div");
    fly.className = "flyout";
    for (const t of g.tools) {
      const item = document.createElement("button");
      item.className = "flyout-item" + (t === g.current ? " active" : "");
      const combo = comboOf(t);
      item.innerHTML = `${Icons[TOOL_ICON[t]]}<span>${labelOf(t)}</span>${combo ? `<span class="kbd">${combo}</span>` : ""}`;
      item.addEventListener("click", () => {
        g.current = t;
        const b = this.buttons.get(g.id)!;
        b.innerHTML = Icons[TOOL_ICON[t]];
        if (g.tools.length > 1) {
          const caret = document.createElement("span");
          caret.className = "flycaret";
          b.appendChild(caret);
        }
        b.title = labelOf(t);
        this.onSelect(t);
        this.closeFlyout();
      });
      fly.appendChild(item);
    }
    document.body.appendChild(fly);
    const r = btn.getBoundingClientRect();
    fly.style.left = r.right + 6 + "px";
    fly.style.top = r.top + "px";
    this.flyout = fly;
  }

  private closeFlyout() {
    if (this.flyout) {
      this.flyout.remove();
      this.flyout = null;
    }
  }

  refresh() {
    const active = this.getActive();
    for (const g of GROUPS) {
      const btn = this.buttons.get(g.id)!;
      const isActive = g.tools.includes(active);
      btn.classList.toggle("active", isActive);
      if (isActive && active !== "cursor") g.current = active;
    }
    // cursor group active when nothing else
    const cursorBtn = this.buttons.get("cursor")!;
    cursorBtn.classList.toggle("active", active === "cursor");
  }
}
